﻿import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '@/context/AuthContext';
import UserAvatarSVG from '@/components/UserAvatarSVG';
import { useState, useEffect, useCallback } from 'react';
import '@/styles/FinanceDashStyles.scss'
import { Tooltip as ReactTooltip } from 'react-tooltip';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import CondensedIncomeStatementTable from '@/pages/Dashboards/ExecutiveDash/CondensedIncomeStatementTable';
import * as Sentry from '@sentry/react';

const incomeList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Insurance Income - Revenue",
    "Covid Credits - Rental",
    "Other Income",
    "Other Contractual Income",
    "Promotional Income",
];

const netMunicipalsList = [
    "Rates Income",
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered",
    "Rates Expense",
    "Electricity Expense",
    "Water Consumption Expense",
    "Sewerage Expense",
    "Refuse Expense"
];

const opExList = [
    "Bank Charges",
    "Cleaning",
    "Insurance",
    "Legal Fees",
    "Management Fee",
    "Marketing",
    "Other Operating Expenses",
    "Repairs & Maintenance",
    "Sanitation & Pest Control",
    "Security",
];

const fuelExpenseList = [
    "Fuel"
];

const nonOpExList = [
    "Audit Secretarial Fees",
    "Professional Fees",
    "SRI Expenses",
    "Depreciation"
];

const netInterestExpenseList = [
    "Interest Received",
    "Interest Paid",
    "Interest Paid InterCo"
];

const revalTaxList = [
    "Revaluation",
    "Profit/ Loss associate",
    "Taxation"
];

const badDebtsList = [
    "Provision for Bad Debts",
    "Bad debts written off",
    "Bad Debts Recovered"
];

const lettingCostList = [
    "Tenant Installations",
    "Consolidation Costs",
    "Letting Commission"
];

const FinanceDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [initialLoading, setInitialLoading] = useState(true);
    const [period, setPeriod] = useState(null);
    const [condensedIncomeData, setCondensedIncomeData] = useState(null);
    const [ratesChartData, setRatesChartData] = useState([]);
    const [elecChartData, setElecChartData] = useState([]);
    const [waterChartData, setWaterChartData] = useState([]);
    const [propertyData, setPropertyData] = useState(null);
    const [totalArea, setTotalArea] = useState(0);
    const [propertyCodes, setPropertyCodes] = useState({});
    const propertyNames = ["Alexandra Plaza", "Boitekong Mall", "Bridge City (New)", "Diepsloot Mall", "Eyethu Orange Farm"
        , "Gateway Mall", "Heidelberg Mall", "Kamaqhekeza", "Kanyamazane Shopping Centre", "KG Mall (Kwa-Guqa)"
        , "King Senzangakona Shopping Centre", "Kuyasa Shopping Centre", "Maxwell Street Shopping Centre", "Mkhuhlu Plaza"
        , "Motherwell Shopping Centre", "Moutse Mall", "Murchison Mall", "Nkomazi Plaza", "Opera Place", "Sam Ntuli"
        , "Setsing IV Shopping Centre", "Sontonga Mall", "The Crossing", "Thulamahashe Plaza"
    ];

    // Fetch the default period when the component mounts
    useEffect(() => {
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch(`/api/Mda/DefaultPeriod`);
                const data = await response.json();
                setPeriod(data.period1);
            } catch (e) {
                Sentry.captureException(e);
                // Only log errors that aren't token refresh failures
                if (e.message !== 'Token refresh failed') {
                    console.error(e);
                }
            }
        };
        fetchDefaultPeriod();
    }, []);

    // Fetch property short codes via MDA external mappings
    useEffect(() => {
        const fetchPropertyShortCodes = async () => {
            try {
                const response = await apiFetch('/api/Property/external-mappings?externalSystem=MDA');
                const data = await response.json();

                if (data.success && data.data) {
                    // Build a mapping of MDA PropertyName -> ShortCode
                    const codesMap = {};
                    data.data.forEach(mapping => {
                        const mdaName = (mapping.externalPropertyName || mapping.ExternalPropertyName)?.trim();
                        const shortCode = mapping.shortCode || mapping.ShortCode;
                        if (mdaName && shortCode) {
                            codesMap[mdaName] = shortCode;
                        }
                    });
                    setPropertyCodes(codesMap);
                }
            } catch (e) {
                Sentry.captureException(e);
                if (e.message !== 'Token refresh failed') {
                    console.error('Error fetching property short codes:', e);
                }
            }
        };
        fetchPropertyShortCodes();
    }, []);

    // State to hold the condensed income statement data
    const fetchCondensedIncomeStatement = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${period}`);
            const data = await response.json();

            if (data.success) {
                setCondensedIncomeData(data.data);
            } else {
                console.error('Failed to fetch trial balance data:', data.message);
            }
        } catch (err) {
            Sentry.captureException(err);
            // Only log errors that aren't token refresh failures
            if (err.message !== 'Token refresh failed') {
                console.error('Error fetching trial balance data:', err);
            }
        }
    }, [apiFetch, period]);

    const fetchPropertyData = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/Mda/PropertyData?Period=${period}`);
            const data = await response.json();

            if (data.success) {
                setPropertyData(data.data);
                const sum = Object.entries(data.data).reduce((acc, [propertyName, area]) => {
                    // Exclude "Unity Centre" and only add if the area is a valid number
                    if (propertyName === "Unity Centre") return acc;
                    const numericArea = Number(area);
                    return acc + (isNaN(numericArea) ? 0 : numericArea);
                }, 0);

                // Round up to the nearest full number, no comma
                setTotalArea(Math.ceil(sum));
            }
        } catch (err) {
            Sentry.captureException(err);
            // Only log errors that aren't token refresh failures
            if (err.message !== 'Token refresh failed') {
                console.error('Error fetching property data:', err);
            }
        }
    }, [apiFetch, period]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setInitialLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!period) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchPropertyData();
        fetchCondensedIncomeStatement();
    }, [period, fetchPropertyData, fetchCondensedIncomeStatement])

    // Functions to calculate recovery rates for properties
    function calculateActualRecoveryRateForProperties(data, propertyNames) {
        if (!Array.isArray(data) || !Array.isArray(propertyNames)) return null;

        const calcForProperty = (propertyName) => {
            const filteredData = data.filter(d => d.PropertyName === propertyName);

            const numerator =
                filteredData.filter(d => d.TransactionCode === 106 || d.TransactionCode === 152)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0)
                +
                filteredData.filter(d => d.TransactionCode === 236)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0);

            const denominator =
                filteredData.filter(d => d.TransactionCode === 350)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0)
                +
                filteredData.filter(d => d.TransactionCode === 344)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0);

            if (denominator === 0) return null;
            return numerator / denominator;
        };

        const rawResult = {};
        propertyNames.forEach(name => {
            rawResult[name] = calcForProperty(name);
        });

        // Transform: abs, *100, round
        const formattedResult = {};
        Object.entries(rawResult).forEach(([key, value]) => {
            formattedResult[key] = value == null ? null : Math.round(Math.abs(value) * 100);
        });

        return formattedResult;
    }

    function calculateBudgetRecoveryRateForProperties(data, propertyNames) {
        if (!Array.isArray(data) || !Array.isArray(propertyNames)) return null;

        const calcForProperty = (propertyName) => {
            const filteredData = data.filter(d => d.PropertyName === propertyName);

            // Numerator: - (sum of BudgetYearToDate for codes 106 and 152)
            const numerator = -(
                filteredData
                    .filter(d => d.TransactionCode === 106 || d.TransactionCode === 152)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0)
            );

            // Denominator: sum of BudgetYearToDate for codes 350 and 344
            const denominator =
                filteredData
                    .filter(d => d.TransactionCode === 350)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0)
                +
                filteredData
                    .filter(d => d.TransactionCode === 344)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0);

            if (denominator === 0) return null;
            return numerator / denominator;
        };

        const rawResult = {};
        propertyNames.forEach(name => {
            rawResult[name] = calcForProperty(name);
        });

        // Format: multiply by 100, round to nearest integer
        const formattedResult = {};
        Object.entries(rawResult).forEach(([key, value]) => {
            formattedResult[key] = value == null ? null : Math.round(value * 100);
        });

        return formattedResult;
    }

    function calculateElectricityRecoveryRateForProperties(data, propertyNames) {
        if (!Array.isArray(data) || !Array.isArray(propertyNames)) return null;

        const calcForProperty = (propertyName) => {
            const filteredData = data.filter(d => d.PropertyName === propertyName);

            // Numerator: - (sum of CurrentBal for code 143)
            const numerator = -(
                filteredData
                    .filter(d => d.TransactionCode === 143)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0)
            );

            // Denominator: sum of CurrentBal for codes 317, 338, 392
            const denominator =
                filteredData
                    .filter(d => d.TransactionCode === 317)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0)
                +
                filteredData
                    .filter(d => d.TransactionCode === 338)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0)
                +
                filteredData
                    .filter(d => d.TransactionCode === 392)
                    .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0);

            if (denominator === 0) return null;
            return numerator / denominator;
        };

        const rawResult = {};
        propertyNames.forEach(name => {
            rawResult[name] = calcForProperty(name);
        });

        // Format: multiply by 100, round to nearest integer
        const formattedResult = {};
        Object.entries(rawResult).forEach(([key, value]) => {
            formattedResult[key] = value == null ? null : Math.round(value * 100);
        });

        return formattedResult;
    }

    function calculateBudgetElectricityRecoveryRateForProperties(data, propertyNames) {
        if (!Array.isArray(data) || !Array.isArray(propertyNames)) return null;

        const calcForProperty = (propertyName) => {
            const filteredData = data.filter(d => d.PropertyName === propertyName);

            // Numerator: - (sum of BudgetYearToDate for code 143)
            const numerator = -(
                filteredData
                    .filter(d => d.TransactionCode === 143)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0)
            );

            // Denominator: sum of BudgetYearToDate for codes 317, 338, 392
            const denominator =
                filteredData
                    .filter(d => d.TransactionCode === 317)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0)
                +
                filteredData
                    .filter(d => d.TransactionCode === 338)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0)
                +
                filteredData
                    .filter(d => d.TransactionCode === 392)
                    .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0);

            if (denominator === 0) return null;
            return numerator / denominator;
        };

        const rawResult = {};
        propertyNames.forEach(name => {
            rawResult[name] = calcForProperty(name);
        });

        // Format: multiply by 100, round to nearest integer
        const formattedResult = {};
        Object.entries(rawResult).forEach(([key, value]) => {
            formattedResult[key] = value == null ? null : Math.round(value * 100);
        });

        return formattedResult;
    }

    function calculateWaterSewerageRecoveryRateForProperties(data, propertyNames) {
        if (!Array.isArray(data) || !Array.isArray(propertyNames)) return null;

        // Numerator codes
        const numeratorCodes = [158, 161];
        // Denominator codes
        const denominatorCodes = [359, 232, 375, 298, 299, 39, 203, 377];

        const calcForProperty = (propertyName) => {
            const filteredData = data.filter(d => d.PropertyName === propertyName);

            // Numerator: sum of CurrentBal for numeratorCodes
            const numerator = filteredData
                .filter(d => numeratorCodes.includes(d.TransactionCode))
                .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0);

            // Denominator: sum of CurrentBal for denominatorCodes
            const denominator = filteredData
                .filter(d => denominatorCodes.includes(d.TransactionCode))
                .reduce((sum, d) => sum + (Number(d.CurrentBal) || 0), 0);

            if (denominator === 0) return null;
            return -numerator / denominator;
        };

        const rawResult = {};
        propertyNames.forEach(name => {
            rawResult[name] = calcForProperty(name);
        });

        // Format: multiply by 100, round to nearest integer
        const formattedResult = {};
        Object.entries(rawResult).forEach(([key, value]) => {
            formattedResult[key] = value == null ? null : Math.round(value * 100);
        });

        return formattedResult;
    }

    function calculateBudgetWaterSewerageRecoveryRateForProperties(data, propertyNames) {
        if (!Array.isArray(data) || !Array.isArray(propertyNames)) return null;

        // Numerator codes
        const numeratorCodes = [158, 161];
        // Denominator codes
        const denominatorCodes = [359, 232, 375, 298, 299, 39, 203, 377];

        const calcForProperty = (propertyName) => {
            const filteredData = data.filter(d => d.PropertyName === propertyName);

            // Numerator: sum of CurrentBal for numeratorCodes
            const numerator = filteredData
                .filter(d => numeratorCodes.includes(d.TransactionCode))
                .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0);

            // Denominator: sum of BudgetYearToDate for denominatorCodes
            const denominator = filteredData
                .filter(d => denominatorCodes.includes(d.TransactionCode))
                .reduce((sum, d) => sum + (Number(d.BudgetYearToDate) || 0), 0);

            if (denominator === 0) return null;
            return -numerator / denominator;
        };

        const rawResult = {};
        propertyNames.forEach(name => {
            rawResult[name] = calcForProperty(name);
        });

        // Format: multiply by 100, round to nearest integer
        const formattedResult = {};
        Object.entries(rawResult).forEach(([key, value]) => {
            formattedResult[key] = value == null ? null : Math.round(value * 100);
        });

        return formattedResult;
    }

    useEffect(() => {
        if (!condensedIncomeData) return;
        const actual = calculateActualRecoveryRateForProperties(condensedIncomeData, propertyNames);
        const budget = calculateBudgetRecoveryRateForProperties(condensedIncomeData, propertyNames);

        const chart = propertyNames.map(name => ({
            property: name,
            actual: actual[name],
            budget: budget[name]
        }));

        const actualElec = calculateElectricityRecoveryRateForProperties(condensedIncomeData, propertyNames);
        const budgetElec = calculateBudgetElectricityRecoveryRateForProperties(condensedIncomeData, propertyNames);
        const elecChart = propertyNames.map(name => ({
            property: name,
            actual: actualElec[name],
            budget: budgetElec[name]
        }));

        const actualWater = calculateWaterSewerageRecoveryRateForProperties(condensedIncomeData, propertyNames);
        const budgetWater = calculateBudgetWaterSewerageRecoveryRateForProperties(condensedIncomeData, propertyNames);
        const waterChart = propertyNames.map(name => ({
            property: name,
            actual: actualWater[name],
            budget: budgetWater[name]
        }));

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRatesChartData(chart);
        setElecChartData(elecChart);
        setWaterChartData(waterChart);
    }, [condensedIncomeData])

    // Function for creating the custom graph x axis labels
    const CustomXAxisTickWithValues = (props) => {
        const { x, y, payload } = props;
        const shortCode = propertyCodes[payload.value];
        const code = shortCode || payload.value.split(' ')[0].substring(0, 3).toUpperCase();
        // Find the data for this property
        const data = elecChartData.find(d => d.property === payload.value);
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="middle" fill="#333" fontWeight="bold">
                    {code}
                </text>
                <text x={0} y={0} dy={32} textAnchor="middle" fill="#F63040" fontSize="12">
                    {data && data.actual != null ? `${data.actual}%` : '-'}
                </text>
                <text x={0} y={0} dy={46} textAnchor="middle" fill="#57565B" fontSize="12">
                    {data && data.budget != null ? `${data.budget}%` : '-'}
                </text>
            </g>
        );
    };

    // Custom XAxis tick for water chart
    const CustomWaterXAxisTickWithValues = (props) => {
        const { x, y, payload } = props;
        const shortCode = propertyCodes[payload.value];
        const code = shortCode || payload.value.split(' ')[0].substring(0, 3).toUpperCase();
        // Find the data for this property in waterChartData
        const data = waterChartData.find(d => d.property === payload.value);
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="middle" fill="#333" fontWeight="bold">
                    {code}
                </text>
                <text x={0} y={0} dy={32} textAnchor="middle" fill="#F63040" fontSize="12">
                    {data && data.actual != null ? `${data.actual}%` : '-'}
                </text>
                <text x={0} y={0} dy={46} textAnchor="middle" fill="#57565B" fontSize="12">
                    {data && data.budget != null ? `${data.budget}%` : '-'}
                </text>
            </g>
        );
    };

    // Custom XAxis tick for rates chart
    const CustomRatesXAxisTickWithValues = (props) => {
        const { x, y, payload } = props;
        const shortCode = propertyCodes[payload.value];
        const code = shortCode || payload.value.split(' ')[0].substring(0, 3).toUpperCase();
        // Find the data for this property in ratesChartData
        const data = ratesChartData.find(d => d.property === payload.value);
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} textAnchor="middle" fill="#333" fontWeight="bold">
                    {code}
                </text>
                <text x={0} y={0} dy={32} textAnchor="middle" fill="#F63040" fontSize="12">
                    {data && data.actual != null ? `${data.actual}%` : '-'}
                </text>
                <text x={0} y={0} dy={46} textAnchor="middle" fill="#57565B" fontSize="12">
                    {data && data.budget != null ? `${data.budget}%` : '-'}
                </text>
            </g>
        );
    };

    const _getGLATooltipHtml = () => {
        if (!propertyData) return '';
        // Table header
        let html = `<table>
        <thead>
            <tr>
                <th>Property</th>
                <th>GLA (m<sup>2</sup>)</th>
            </tr>
        </thead>
        <tbody>`;
        // Table rows
        html += Object.entries(propertyData)
            .filter(([name]) => name !== "Unity Centre")
            .map(([name, area]) => {
                const formatted = Number(area)
                    .toLocaleString('fr-FR', { maximumFractionDigits: 0 })
                    .replace(/\u202F/g, '  ');
                return `<tr>
                <td>${name}</td>
                <td>${formatted}</td>
            </tr>`;
            })
            .join('');
        html += '</tbody></table>';
        return html;
    };

    const _formatNumberWithSpace = (value) => {
        if (typeof value !== 'number') return value;
        // Format with fr-FR to get the grouping
        let formatted = value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
        return formatted.replace(/\u202F/g, '  ');
    };

    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthIndex = parseInt(month, 10);
        return {
            monthName: monthNames[monthIndex],
            year,
            prevYear
        };
    }

    const { monthName: _monthName, year: _year } = getMonthYear(period);

    const _getIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += incomeList.map(caption => {
            // Sum all items for this caption
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            // Convert to millions and format
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _calculateIncome = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        // Filter items by Caption or MdaAccountName that match incomeList
        const incomeItems = condensedIncomeData.filter(item =>
            incomeList.includes(item.Caption)
        );

        // Sum values and convert to millions with 1 decimal place
        if (incomeItems.length > 0) {
            const total = incomeItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            // Format to millions with one decimal place
            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }

        return "0.0";
    };

    const _getVarianceTooltipHtml = (actual, budget, labelActual = "Actual YTD", labelBudget = "Budget YTD") => {
        return `
        <table>
            <thead>
                <tr>
                    <th>Value</th>
                    <th>Amount (R'm)</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>${labelActual}</td>
                    <td>${actual}</td>
                </tr>
                <tr>
                    <td>${labelBudget}</td>
                    <td>${budget}</td>
                </tr>
            </tbody>
        </table>
    `;
    };

    const _calculateVariance = (actual, budget) => {
        if (actual === "-" || budget === "-") return "-";

        const actualValue = parseFloat(actual.replace(/\s/g, ''));
        const budgetValue = parseFloat(budget.replace(/\s/g, ''));

        if (budgetValue === 0) return "-";

        const variance = ((actualValue - budgetValue) / budgetValue) * 100;
        return variance.toFixed(1) + "%";
    };

    const _parseIncomeValue = (val) => {
        if (typeof val === "number") return val;
        if (typeof val === "string") {
            // Remove spaces, commas, and handle "-" as 0
            const cleaned = val.replace(/[\s,']/g, '');
            if (cleaned === "-" || cleaned === "") return 0;
            const num = parseFloat(cleaned);
            return isNaN(num) ? 0 : num;
        }
        return 0;
    };

    const _getForecastTooltipHtml = (v1, v2, v3, v4, result) => `
    <table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>
            <tr><td>Budget Full Year Revised</td><td>${v1.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td></tr>
            <tr><td>Budget Full Year</td><td>${v2.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td></tr>
            <tr><td>- Budget Year To Date</td><td>${v3.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td></tr>
            <tr><td>+ Current Bal</td><td>${v4.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td></tr>
            <tr style="font-weight:bold;"><td>Total</td><td>${result.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</td></tr>
        </tbody>
    </table>
`   ;

    const _getNetMunicipalsTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += netMunicipalsList.map(caption => {
            // Sum all items for this caption
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            // Convert to millions and format
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _calculateNetMunicipals = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const municipalItems = condensedIncomeData.filter(item =>
            netMunicipalsList.includes(item.Caption) || netMunicipalsList.includes(item.MdaAccountName)
        );

        if (municipalItems.length > 0) {
            const total = municipalItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const _getOperatingExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';

        // Helper to sum and format a list
        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return {
                sum,
                formatted: inMillions.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/\u202F/g, '  ')
            };
        };

        const opEx = sumAndFormat(opExList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);

        const total = opEx.sum + badDebts.sum + lettingCost.sum;
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).replace(/\u202F/g, '  ');

        let html = `<table>
        <thead>
            <tr>
                <th>Component</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>
            <tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>
            <tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>
            <tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>
            <tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>
        </tbody>
    </table>`;
        return html;
    };

    const _calculateOperatingEx = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        // Helper to sum a list
        const sumList = (list) => {
            return condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((sum, item) => {
                    const value = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (value * -1);
                }, 0);
        };

        const opEx = sumList(opExList);
        const badDebts = sumList(badDebtsList);
        const lettingCost = sumList(lettingCostList);

        const total = opEx + badDebts + lettingCost;
        const inMillions = total / 1000000;

        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const _getFuelTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += fuelExpenseList.map(caption => {
            // Sum all items for this caption
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            // Convert to millions and format
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _calculateFuel = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const fuelItems = condensedIncomeData.filter(item =>
            fuelExpenseList.includes(item.Caption)
        );

        if (fuelItems.length > 0) {
            const total = fuelItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const _getNetOperatingIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        // Helper to sum and format a category
        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _calculateNetOperatingIncome = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        // Calculate the sum for each list and add them together
        const incomeSum = sumCategoryData(incomeList, valueField);
        const municipalsSum = sumCategoryData(netMunicipalsList, valueField);
        const opExSum = sumCategoryData(opExList, valueField);
        const fuelSum = sumCategoryData(fuelExpenseList, valueField);
        const badDebts = sumCategoryData(badDebtsList, valueField);
        const lettingCosts = sumCategoryData(lettingCostList, valueField);

        // Total Net Operating Income
        const total = incomeSum + municipalsSum + opExSum + fuelSum + badDebts + lettingCosts;

        // Format to millions with one decimal place
        const inMillions = total / 1000000;
        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const sumCategoryData = (categoryList, valueField) => {
        if (!condensedIncomeData) {
            return 0;
        }

        const items = condensedIncomeData.filter(item =>
            categoryList.includes(item.Caption)
        );

        if (items.length === 0) {
            return 0;
        }

        return items.reduce((sum, item) => {
            const value = typeof item[valueField] === 'number'
                ? item[valueField]
                : parseFloat(item[valueField] || '0');
            return sum + (value * -1);
        }, 0);
    };

    const _getNonOperatingExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        let total = 0;
        html += nonOpExList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption || i.MdaAccountName === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            total += value;
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        // Add total row
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).replace(/\u202F/g, '  ');
        html += `<tr style="font-weight:bold;">
        <td>Total</td>
        <td>${totalFormatted}</td>
    </tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _calculateNonOperatingEx = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const nonOpExItems = condensedIncomeData.filter(item =>
            nonOpExList.includes(item.Caption)
        );

        if (nonOpExItems.length > 0) {
            const total = nonOpExItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const _getNetOperatingIncomeBeforeFinanceTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);
        const nonOpEx = sumAndFormat(nonOpExList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;
        html += `<tr><td>Non operating expenses</td><td>${nonOpEx.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum + nonOpEx.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _calculateNetOperatingIncomeBeforeFinance = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const netOperatingIncomeSum = sumCategoryData(incomeList, valueField) +
            sumCategoryData(netMunicipalsList, valueField) +
            sumCategoryData(opExList, valueField) +
            sumCategoryData(fuelExpenseList, valueField) +
            sumCategoryData(badDebtsList, valueField) +
            sumCategoryData(lettingCostList, valueField);

        const nonOpExSum = sumCategoryData(nonOpExList, valueField);

        const total = netOperatingIncomeSum + nonOpExSum;

        const inMillions = total / 1000000;
        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    const _getNetInterestExTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        let total = 0;
        html += netInterestExpenseList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            total += value;
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        // Add total row
        const totalFormatted = (total / 1000000).toLocaleString('fr-FR', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).replace(/\u202F/g, '  ');
        html += `<tr style="font-weight:bold;">
        <td>Total</td>
        <td>${totalFormatted}</td>
    </tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _calculateNetInterestEx = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const netInterestItems = condensedIncomeData.filter(item =>
            netInterestExpenseList.includes(item.Caption)
        );

        if (netInterestItems.length > 0) {
            const total = netInterestItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const _getRevalAndTaxTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
        <thead>
            <tr>
                <th>Caption</th>
                <th>Amount (R'm)</th>
            </tr>
        </thead>
        <tbody>`;
        html += revalTaxList.map(caption => {
            const items = condensedIncomeData.filter(
                i => i.Caption === caption
            );
            let value = 0;
            if (items.length > 0) {
                value = items.reduce((sum, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return sum + (v * -1);
                }, 0);
            }
            const inMillions = value / 1000000;
            const formatted = inMillions.toLocaleString('fr-FR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            }).replace(/\u202F/g, '  ');
            return `<tr>
            <td>${caption}</td>
            <td>${formatted}</td>
        </tr>`;
        }).join('');
        html += '</tbody></table>';
        return html;
    };

    const _calculateRevalAndTax = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const revalAndTaxItems = condensedIncomeData.filter(item =>
            revalTaxList.includes(item.Caption)
        );

        if (revalAndTaxItems.length > 0) {
            const total = revalAndTaxItems.reduce((sum, item) => {
                const value = typeof item[valueField] === 'number'
                    ? item[valueField]
                    : parseFloat(item[valueField] || '0');
                return sum + (value * -1);
            }, 0);

            const inMillions = total / 1000000;
            return inMillions.toLocaleString('en-ZA', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1
            });
        }
        return "0.0";
    };

    const _getNetIncomeTooltipHtml = (valueField) => {
        if (!condensedIncomeData) return '';
        let html = `<table>
    <thead>
        <tr>
            <th>Component</th>
            <th>Amount (R'm)</th>
        </tr>
    </thead>
    <tbody>`;

        // Helper to sum and format a category
        const sumAndFormat = (list) => {
            const sum = condensedIncomeData
                .filter(item => list.includes(item.Caption))
                .reduce((acc, item) => {
                    const v = typeof item[valueField] === 'number'
                        ? item[valueField]
                        : parseFloat(item[valueField] || '0');
                    return acc + (v * -1);
                }, 0);
            const inMillions = sum / 1000000;
            return { sum, formatted: inMillions.toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
        };

        const income = sumAndFormat(incomeList);
        const municipals = sumAndFormat(netMunicipalsList);
        const opEx = sumAndFormat(opExList);
        const fuel = sumAndFormat(fuelExpenseList);
        const badDebts = sumAndFormat(badDebtsList);
        const lettingCost = sumAndFormat(lettingCostList);
        const nonOpEx = sumAndFormat(nonOpExList);
        const interest = sumAndFormat(netInterestExpenseList);
        const revalTax = sumAndFormat(revalTaxList);

        html += `<tr><td>Income</td><td>${income.formatted}</td></tr>`;
        html += `<tr><td>Net municipals</td><td>${municipals.formatted}</td></tr>`;
        html += `<tr><td>Operating expenses (excl. fuel)</td><td>${opEx.formatted}</td></tr>`;
        html += `<tr><td>Fuel expense</td><td>${fuel.formatted}</td></tr>`;
        html += `<tr><td>Bad debts</td><td>${badDebts.formatted}</td></tr>`;
        html += `<tr><td>Letting cost</td><td>${lettingCost.formatted}</td></tr>`;
        html += `<tr><td>Non operating expenses</td><td>${nonOpEx.formatted}</td></tr>`;
        html += `<tr><td>Net interest expense</td><td>${interest.formatted}</td></tr>`;
        html += `<tr><td>Revaluation and tax</td><td>${revalTax.formatted}</td></tr>`;

        const total = income.sum + municipals.sum + opEx.sum + fuel.sum + badDebts.sum + lettingCost.sum + nonOpEx.sum + interest.sum + revalTax.sum;
        const totalFormatted = (total / 1000000).toLocaleString('en-ZA', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

        html += `<tr style="font-weight:bold;"><td>Total</td><td>${totalFormatted}</td></tr>`;
        html += '</tbody></table>';
        return html;
    };

    const _calculateNetIncome = (valueField) => {
        if (!condensedIncomeData) {
            return "-";
        }

        const netOperatingIncome = sumCategoryData(incomeList, valueField) +
            sumCategoryData(netMunicipalsList, valueField) +
            sumCategoryData(opExList, valueField) +
            sumCategoryData(fuelExpenseList, valueField) +
            sumCategoryData(badDebtsList, valueField) +
            sumCategoryData(lettingCostList, valueField);

        const nonOpEx = sumCategoryData(nonOpExList, valueField);

        const netIncomeBeforeInterest = netOperatingIncome + nonOpEx;

        const interest = sumCategoryData(netInterestExpenseList, valueField);

        const total = netIncomeBeforeInterest + interest

        const inMillions = total / 1000000;
        return inMillions.toLocaleString('en-ZA', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        });
    };

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                    <div className="finance-dashboard">
                        <div className="dashboard-greeting">
                            <UserAvatarSVG />
                            Hello {user.FirstName || user.firstName}
                        </div>
                        <div><h3>Finance Dashboard</h3></div>
                        <div className="findashboard-section">
                            {/*Condensed Income Statement table*/}
                            <CondensedIncomeStatementTable
                                condensedIncomeData={condensedIncomeData}
                                period={period}
                                totalArea={totalArea}
                                propertyData={propertyData}
                            />
                        </div>
                        <div className="findashboard-section data-graph">
                            <div className="graph-heading">
                                YTD Rates Expense % Recovery
                            </div>
                            <div style={{ width: '100%', height: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={ratesChartData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 40 }} 
                                    >
                                        <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4" />
                                        <XAxis
                                            dataKey="property"
                                            height={70}
                                            tick={CustomRatesXAxisTickWithValues}
                                            interval={0}
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="actual" fill="#F63040" name="Actual %" />
                                        <Bar dataKey="budget" fill="#57565B" name="Budget %" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="findashboard-section data-graph">
                            <div className="graph-heading">
                                YTD Electricity Expense % Recovery
                            </div>
                            <div style={{ width: '100%', height: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={elecChartData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                                    >
                                        <CartesianGrid stroke="#000" vertical={false} fill="#f4f4f4" />
                                        <XAxis
                                            dataKey="property"
                                            height={70}
                                            tick={CustomXAxisTickWithValues}
                                            interval={0}
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="actual" fill="#F63040" name="Actual %" />
                                        <Bar dataKey="budget" fill="#57565B" name="Budget %" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="findashboard-section data-graph">
                            <div className="graph-heading">
                                YTD Water &amp; Sewerage Expense % Recovery
                            </div>
                            <div style={{ width: '100%', height: 400 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={waterChartData}
                                        margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                                    >
                                        <CartesianGrid stroke="#000" vertical={false}  fill="#f4f4f4" />
                                        <XAxis
                                            dataKey="property"
                                            height={70}
                                            tick={CustomWaterXAxisTickWithValues}
                                            interval={0}
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="actual" fill="#F63040" name="Actual %" />
                                        <Bar dataKey="budget" fill="#57565B" name="Budget %" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <ReactTooltip
                            id="tt"
                            place="bottom"
                            variant="dark"
                            delayShow={150}
                            delayHide={0}
                            openEvents={{ mouseenter: true }}
                            closeEvents={{ mouseleave: true }}
                            globalCloseEvents={{ escape: true, scroll: true, resize: true, clickOutsideAnchor: true }}
                            render={({ content }) =>
                                content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null
                            }
                        />
                    </div>
            )}
        </>
    )
}

export default FinanceDashboard;